import { authApi } from "@api";
import { WSBalanceChangedEvent } from "@constants/ws";
import authSlice from "@stores/auth/slices";
import { bnOrZero } from "kz-ui-sdk";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useWSBalanceChanged } from "./useAppWS";
import useTypedSelector from "./useTypedSelector";

export interface UseProfileBalanceProps {
  delayUpdate?: number;
  skipAutoUpdate?: boolean;
}

export default function useProfileBalance({ delayUpdate, skipAutoUpdate }: UseProfileBalanceProps = {}) {
  const { self } = useTypedSelector((state) => state.auth);
  const [getProfile, { isLoading }] = authApi.useLazyGetProfileQuery();

  const balance = useMemo(() => {
    return bnOrZero(self?.member?.primaryWallet?.balance);
  }, [self?.member?.primaryWallet?.balance]);

  const dispatch = useDispatch();

  const [prevBalance, setPrevBalance] = useState(() => balance);
  const [nextBalance, setNextBalance] = useState(() => balance);

  useEffect(() => {
    if (skipAutoUpdate) return;
    setPrevBalance(nextBalance);
    setNextBalance(balance);
    // To make number run
    // eslint-disable-next-line
  }, [balance, skipAutoUpdate]);

  const refetch = useCallback(async () => {
    const profileResult = await getProfile();
    if (profileResult?.data) {
      dispatch(authSlice?.actions?.updateProfile(profileResult.data));
    }
  }, [dispatch, getProfile]);

  const handleBalanceChange = useCallback(
    (event: WSBalanceChangedEvent) => {
      const balance = event.detail.balance;
      setTimeout(() => {
        // update balance immediately
        // if player doesn't have primary wallet, create a temp primary wallet for this session
        dispatch(authSlice.actions.updateProfileBalance(balance));
      }, delayUpdate ?? 0);
    },
    [delayUpdate, dispatch],
  );

  useWSBalanceChanged({ cb: handleBalanceChange });

  return { balance, refetch, prevBalance, nextBalance, isLoading };
}
